import React from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  MessageSquare,
  Mic,
  BarChart3,
  Bot,
  Receipt,
  ArrowRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

// Subtle graph-paper grid behind the centered card, matching the Figma
// install-success design. Kept as a plain CSS background (border-color
// token at low opacity) rather than a new asset/component.
const GRID_BACKGROUND_STYLE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)',
  backgroundSize: '40px 40px',
}

const WHAT_YOU_CAN_DO = [
  {
    icon: MessageSquare,
    title: 'Use the /standup command',
    description: 'Kick off an async standup in any Slack channel.',
  },
  {
    icon: Mic,
    title: 'Record voice memos',
    description: "Drop a 20-second voice note, we transcribe and file it.",
  },
  {
    icon: BarChart3,
    title: 'View analytics',
    description: 'Track standup trends, sentiment, and blockers reported.',
  },
  {
    icon: Bot,
    title: 'Connector reports in Slack',
    description: 'Your AI connectors share progress, results, and blockers in Slack.',
  },
  {
    icon: Receipt,
    title: "See exactly what you're spending",
    description: 'Full visibility into token spend, per person and per connector, in real time.',
  },
]

const InstallSuccess: React.FC = () => {
  const [searchParams] = useSearchParams()
  const teamName = searchParams.get('team') || 'Your team'
  const error = searchParams.get('error')

  if (error) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background px-4 py-12"
        style={GRID_BACKGROUND_STYLE}
      >
        {/* No border-radius, per Figma (2026-09-29) -- was rounded-2xl. */}
        <div className="w-full max-w-md border border-border bg-card p-8 shadow-sm">
          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-9 w-9 text-destructive" />
            </div>
          </div>
          <h1 className="mt-6 text-center text-2xl font-semibold text-foreground">
            Installation Failed
          </h1>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            There was an issue installing SyncSimple to your workspace.
          </p>

          <div className="mt-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div>
                <h3 className="text-sm font-medium text-foreground">Error details</h3>
                <p className="mt-1 text-sm text-muted-foreground">{decodeURIComponent(error)}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-3 text-sm text-muted-foreground">
            <p>Try installing the app again using the correct installation link.</p>
            <p>Make sure you have admin permissions in your Slack workspace.</p>
            <p>Contact support if the issue persists.</p>
          </div>

          <Button
            asChild
            size="lg"
            className="mt-6 w-full bg-ink-900 text-paper-50 hover:bg-ink-800"
          >
            <Link to="/dashboard">Return to your workspace</Link>
          </Button>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Need help?{' '}
            <a href="#" className="text-primary hover:underline">
              Contact support
            </a>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background px-4 py-12"
      style={GRID_BACKGROUND_STYLE}
    >
      {/* No border-radius, per Figma (2026-09-29) -- was rounded-2xl. */}
      <div className="w-full max-w-md border border-border bg-card p-8 shadow-sm">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/15">
            <CheckCircle2 className="h-9 w-9 text-success" />
          </div>
        </div>
        <h1 className="mt-6 text-center text-2xl font-semibold text-foreground">
          You're connected to Slack!
        </h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          SyncSimple is installed for <strong className="text-foreground">{teamName}</strong>.
          Let's set up your first standup.
        </p>

        <div className="mt-6 rounded-xl border border-signal-cyan/20 bg-signal-cyan/5 p-5">
          <h2 className="border-b border-signal-cyan/20 pb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            You're all set. Here's what you can do now:
          </h2>
          <ul className="mt-4 space-y-4">
            {WHAT_YOU_CAN_DO.map(({ icon: Icon, title, description }) => (
              <li key={title} className="flex items-start gap-3">
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <div>
                  <h3 className="text-sm font-medium text-foreground">{title}</h3>
                  <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-6 text-sm text-muted-foreground">
          <strong className="text-foreground">Pro Tip:</strong> Set a recurring Slack reminder or
          create a workflow so your team submits stand-ups daily.
        </p>

        <Button asChild size="lg" className="mt-6 w-full bg-ink-900 text-paper-50 hover:bg-ink-800">
          <Link to="/dashboard">
            Go to Dashboard
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  )
}

export default InstallSuccess
